import { Column } from '@tanstack/react-table';
import { FunctionComponent, useState } from 'react';
import { SearchProvider, useSearch } from '../context/SearchInputContext';
import { DebouncedInput } from './DebouncedInput';
import { LocalEditType } from '../types/editable.types';
import { Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Keep this export for backward compatibility

function FilterColumn({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant } = column.columnDef.meta ?? {};

  return filterVariant === 'inNumberRange' ? (
    <div className="flex w-full gap-2">
      <DebouncedInput
        type="number"
        value={(columnFilterValue as [number, number])?.[0] ?? ''}
        onChange={(value) => {
          column.setFilterValue((old: [number | undefined, number | undefined]) => {
            const min = value === '' || value === null ? undefined : Number(value);
            const max = old?.[1];
            if (min == null && max == null) return undefined;
            return [min, max];
          });
        }}
        placeholder="Min"
        className="w-full rounded-lg border border-gray-200 bg-white/50 px-3 py-1.5 text-sm font-medium focus:border-mainblue/30 focus:outline-none focus:ring-2 focus:ring-mainblue/20"
      />

      <DebouncedInput
        type="number"
        value={(columnFilterValue as [number, number])?.[1] ?? ''}
        onChange={(value) => {
          column.setFilterValue((old: [number | undefined, number | undefined]) => {
            const min = old?.[0];
            const max = value === '' || value === null ? undefined : Number(value);
            if (min == null && max == null) return undefined;
            return [min, max];
          });
        }}
        placeholder="Max"
        className="w-full rounded-lg border border-gray-200 bg-white/50 px-3 py-1.5 text-sm font-medium focus:border-mainblue/30 focus:outline-none focus:ring-2 focus:ring-mainblue/20"
      />
    </div>
  ) : filterVariant === 'select' ? (
    <SearchProvider>
      <SelectColumnItems column={column} />
    </SearchProvider>
  ) : (
    <DebouncedInput
      className="w-full rounded-lg border border-gray-200 bg-white/50 px-3 py-1.5 text-sm font-medium focus:border-mainblue/30 focus:outline-none focus:ring-2 focus:ring-mainblue/20"
      onChange={(value) => {
        if (!value) return;
        column.setFilterValue(value);
      }}
      placeholder="Search..."
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
  );
}

export default FilterColumn;

function SearchFilterColumnItems() {
  const { searchTerm, setSearchTerm } = useSearch();

  function handleChange(value: string | number) {
    setSearchTerm(value as string);
  }

  return (
    <DebouncedInput
      className="w-full rounded-lg border border-gray-200 bg-white/50 px-3 py-1.5 text-sm font-medium focus:border-mainblue/30 focus:outline-none focus:ring-2 focus:ring-mainblue/20"
      onChange={handleChange}
      placeholder="Search..."
      type="text"
      value={searchTerm}
    />
  );
}

function SelectColumnItems({ column }: { column: Column<any, unknown> }) {
  const { searchTerm } = useSearch();

  const columnFilterValue =
    (column.getFilterValue() as (string | number)[]) || [];
  const allColumnValuesArray = Array.from(
    column.getFacetedUniqueValues().keys(),
  ).filter((v) => v !== '' && v !== null && v !== undefined);
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  const processedData: string[] = allColumnValuesArray.reduce((acc, val) => {
    if (!val) return acc;

    let formattedVal = typeof val === 'number' ? String(val) : val;
    if (isLocalEditType(formattedVal)) formattedVal = formattedVal.updated;

    const lowerCaseKey = formattedVal.toLowerCase();

    if (
      lowerCaseKey.includes(lowerCaseSearchTerm) ||
      columnFilterValue.some((value) =>
        lowerCaseKey.includes(String(value).toLowerCase()),
      )
    ) {
      acc.push(formattedVal);
    }

    return acc;
  }, []);

  const visibleOptions = processedData.sort((a, b) => a.localeCompare(b));
  const notListed = columnFilterValue.filter(
    (filter) => !allColumnValuesArray.includes(filter),
  );

  const isAllVisibleSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((item) => columnFilterValue.includes(item));

  const isAllSelectableSelected =
    allColumnValuesArray.length > 0 &&
    allColumnValuesArray.every((item) => columnFilterValue.includes(item));

  const handleSelectAll = () => {
    if (isAllSelectableSelected) {
      column.setFilterValue(
        (prev: string[]) => prev?.filter((el) => notListed.includes(el)) || [],
      );
    } else {
      column.setFilterValue([...allColumnValuesArray, ...notListed]);
    }
  };

  const toggleItem = (item: string) => {
    column.setFilterValue((prev: string[]) => {
      if (!prev) return [item];
      return prev.includes(item)
        ? prev.filter((el) => el !== item)
        : [...prev, item];
    });
  };

  return (
    <div className="w-100 flex flex-col">
      {notListed.length > 0 && (
        <div className="mb-2 rounded-md border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <div className="text-sm font-medium text-gray-700">
              Active Filters{' '}
              <span className="mr-4 text-[10px] text-gray-500">
                (not found in current fund / date)
              </span>
            </div>
            <button
              onClick={() => {
                column.setFilterValue((prev: string[]) =>
                  prev?.filter((el) => !notListed.includes(el)),
                );
              }}
              className="rounded-sm px-2 py-1 text-xs text-gray-500 hover:bg-gray-200 hover:text-gray-700"
            >
              Remove All
            </button>
          </div>
          <div className="max-h-20 overflow-y-auto px-3 pb-2">
            {notListed.map((value) => (
              <div
                key={value}
                className="group relative flex cursor-default items-center gap-1.5 rounded-sm bg-gray-100 py-1.5 pl-2.5 pr-6 text-xs font-medium"
              >
                <span className="text-gray-700">{value}</span>
                <div
                  className="absolute right-1 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer rounded-sm bg-white/80 opacity-0 transition-all hover:bg-white group-hover:opacity-100"
                  onClick={() => {
                    column.setFilterValue((prev: string[]) =>
                      prev?.filter((el) => el !== value),
                    );
                  }}
                >
                  <CloseIcon className="h-3 w-3 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-md border border-gray-200">
        <div className="flex items-center gap-2 border-b border-gray-200 p-2">
          <SearchFilterColumnItems />
          <div className="flex items-center gap-1">
            <Checkbox
              checked={isAllVisibleSelected}
              onChange={handleSelectAll}
              className="p-0.5 text-mainblue"
              size="small"
            />
            <span className="whitespace-nowrap pr-1 text-xs font-medium text-gray-700">
              Select All
            </span>
          </div>
        </div>

        <div className="px-3 text-xs text-gray-500">
          <span className="font-medium text-mainblue">
            {
              columnFilterValue.filter(
                (item) => !notListed.includes(String(item)),
              ).length
            }{' '}
            selected
          </span>
          {visibleOptions.length > 0 && <span className="mx-1">•</span>}
          <span>{visibleOptions.length} total</span>
        </div>

        <div
          className={`scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-gray-50 overflow-y-auto p-2 ${notListed.length > 0 ? 'max-h-[7.5rem]' : 'max-h-48'}`}
        >
          <div className="flex flex-col gap-1 rounded-sm">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((key, index) => (
                <OptionItem
                  key={index}
                  item={key}
                  selected={columnFilterValue.includes(key)}
                  onToggle={() => toggleItem(key)}
                />
              ))
            ) : (
              <div className="flex items-center justify-center p-4 text-sm text-gray-500">
                No options available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface OptionItemProps {
  item: string;
  selected: boolean;
  onToggle: () => void;
}

const OptionItem: FunctionComponent<OptionItemProps> = ({
  item,
  selected,
  onToggle,
}) => {
  return (
    <div
      className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm font-medium transition-colors hover:bg-gray-50/80 ${selected ? 'bg-gray-100 font-semibold text-gray-700' : 'text-gray-600'
        }`}
      onClick={onToggle}
    >
      <span className="flex-grow">{item}</span>
    </div>
  );
};
export function isLocalEditType(obj: any): obj is LocalEditType {
  return (
    obj &&
    (typeof obj.old === 'string' || typeof obj.old === 'number') &&
    (typeof obj.updated === 'string' || typeof obj.updated === 'number')
  );
}

export function isLocalEditTypeAndValueIsEmpty(obj: any): boolean {
  return isLocalEditType(obj) && (obj.updated === '' || obj.updated == null);
}
